import React, { useEffect, useState, useRef } from 'react';
import professionalProfileImage from '../assets/IMG_9835_inPixio.png';
import { aboutProfile, aboutIntro } from '../content/aboutContent';
import { careerWorkHistory } from '../content/careerContent';
import { projectsItems, projectsGithubLink } from '../content/projectsContent';
import { hobbyItems, hobbiesDescription } from '../content/hobbiesContent';
import ContactLinks from '../components/ContactLinks';
import '../styles/ClassicScroll.css';

/* ── Constants ──────────────────────────────────────────────────────────── */

const SUBTITLE_PHRASES = [
  'AI & Automation Engineer',
  'Software Engineer',
  'GCP · AWS · Firebase · FastAPI',
  'Building Reliable Automation',
];

const METRICS = [
  { value: '$200k+', label: 'Monthly Payments Processed' },
  { value: '25,000+', label: 'Workers Scaled To' },
  { value: '4+',     label: 'Years of Experience' },
  { value: '200+',   label: 'Gigs Automatically Managed Weekly' },
];

/** Resume-aligned skill groups; “Platforms & integrations” captures stack from shipped work. */
const CERTIFICATIONS = [
  {
    short: 'Solutions Architect – Associate',
    full: 'AWS Certified Solutions Architect – Associate',
  },
  {
    short: 'Cloud Practitioner',
    full: 'AWS Certified Cloud Practitioner',
  },
];

const TECH_STACK = [
  {
    category: 'Languages',
    color: '#a78bfa',
    skills: ['Python', 'Java', 'TypeScript', 'JavaScript', 'SQL', 'C#'],
  },
  {
    category: 'Backend & APIs',
    color: '#34d399',
    skills: [
      'FastAPI',
      'Spring Boot',
      'Node.js',
      'REST APIs',
      'Webhooks',
      'Auth / access control',
    ],
  },
  {
    category: 'Datastores & DevOps',
    color: '#fbbf24',
    skills: ['Firestore', 'PostgreSQL', 'Docker', 'CI/CD', 'Git', 'Jira'],
  },
  {
    category: 'Platforms & integrations',
    color: '#818cf8',
    skills: [
      'React',
      'Firebase',
      'BigQuery',
      'Looker Studio',
      'Stripe',
      'Twilio',
      'Jenkins',
      'Power Platform',
      'Socket.IO',
    ],
  },
];

const EDUCATION = {
  school: 'Carleton University',
  degree: 'Bachelor of Engineering — Computer Software Engineering (Co-op)',
  date: 'May 2025',
  location: 'Ottawa / Toronto, ON',
};

const PROJECT_TECH = {
  'BattleBees Multiplayer':            ['React', 'TypeScript', 'Socket.IO', 'Firebase'],
  'PIE — Portfolio Intelligence Engine': ['React', 'TypeScript', 'FastAPI', 'Firebase', 'OpenAI'],
  'EndpointExplorer':                  ['C#', '.NET', 'Uno Platform', 'OpenAPI', 'Python'],
  'Elevator System':                   ['Java', 'Multithreading', 'UDP'],
  'Monopoly':                          ['Java', 'Swing GUI'],
  'Soccer Database':                   ['PostgreSQL', 'SQL', 'Python'],
};

const HOBBY_ICONS = {
  reading: '📚',
  leetcode: '💻',
  lego: '🧱',
  movies: '🎬',
  sports: '🏀',
};

/* ── Scroll-reveal hook ─────────────────────────────────────────────────── */

function useScrollReveal(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible];
}

/* ── Reusable section wrapper with reveal ───────────────────────────────── */

function RevealSection({ id, className, children }) {
  const [ref, visible] = useScrollReveal();
  return (
    <section
      id={id}
      ref={ref}
      className={`classic-section${className ? ` ${className}` : ''}${visible ? ' is-visible' : ''}`}
    >
      {children}
    </section>
  );
}

/* ── Hero ───────────────────────────────────────────────────────────────── */

function HeroSection() {
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const id = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setPhraseIdx(i => (i + 1) % SUBTITLE_PHRASES.length);
        setFade(true);
      }, 380);
    }, 3200);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="about" className="classic-hero">
      <div className="classic-hero-inner">

        {/* Photo */}
        <div className="classic-hero-photo-col">
          <div className="classic-hero-photo-frame">
            <img
              src={professionalProfileImage}
              alt={`${aboutProfile.name} profile`}
              className="classic-hero-photo"
            />
          </div>
        </div>

        {/* Text */}
        <div className="classic-hero-text-col">
    

          <h1 className="classic-hero-name">
            <span className="classic-hero-first">Quinton</span>{' '}
            <span className="classic-hero-last">Tracey</span>
          </h1>

          <p className={`classic-hero-subtitle${fade ? ' fade-in' : ' fade-out'}`}>
            <span className="classic-mono-prefix">$&nbsp;</span>
            {SUBTITLE_PHRASES[phraseIdx]}
            <span className="classic-cursor">_</span>
          </p>

          <p className="classic-hero-tagline">{aboutIntro}</p>

          {/* Key metrics */}
          <div className="classic-hero-metrics">
            {METRICS.map(m => (
              <div key={m.label} className="classic-metric-card">
                <span className="classic-metric-value">{m.value}</span>
                <span className="classic-metric-label">{m.label}</span>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="classic-hero-ctas">
            <a href="#projects" className="classic-cta-primary">View Projects</a>
            <a href="#contact"  className="classic-cta-secondary">Contact Me</a>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ── Tech Stack ─────────────────────────────────────────────────────────── */

function SkillsSection() {
  return (
    <RevealSection id="tech-stack" className="classic-skills-section">
      <div className="classic-section-inner">
        <div className="classic-section-header">
          <span className="classic-section-label">// 01</span>
          <h2 className="classic-section-title">Tech Stack</h2>
        </div>

        <div className="classic-cert-block" aria-labelledby="classic-cert-heading">
          <h3 id="classic-cert-heading" className="classic-subsection-title">
            Certifications
          </h3>
          <div className="classic-cert-grid">
            {CERTIFICATIONS.map((c) => (
              <div key={c.full} className="classic-cert-card">
                <span className="classic-cert-badge" aria-hidden="true">AWS</span>
                <div className="classic-cert-body">
                  <p className="classic-cert-name">{c.short}</p>
                  <p className="classic-cert-full">{c.full}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="classic-skills-grid">
          {TECH_STACK.map(group => (
            <div
              key={group.category}
              className="classic-skill-group"
              style={{ '--group-color': group.color }}
            >
              <h3 className="classic-skill-group-title">{group.category}</h3>
              <div className="classic-skill-tags">
                {group.skills.map(skill => (
                  <span key={skill} className="classic-skill-tag">{skill}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="classic-education-block">
          <h3 className="classic-subsection-title">Education</h3>
          <div className="classic-education-card">
            <p className="classic-education-school">{EDUCATION.school}</p>
            <p className="classic-education-degree">{EDUCATION.degree}</p>
            <p className="classic-education-meta">
              <span className="classic-mono-text">{EDUCATION.date}</span>
              <span className="classic-education-sep" aria-hidden="true">·</span>
              <span>{EDUCATION.location}</span>
            </p>
          </div>
        </div>
      </div>
    </RevealSection>
  );
}

/* ── Experience / Timeline ──────────────────────────────────────────────── */

function ExperienceSection() {
  return (
    <RevealSection id="career" className="classic-experience-section">
      <div className="classic-section-inner">
        <div className="classic-section-header">
          <span className="classic-section-label">// 02</span>
          <h2 className="classic-section-title">Career</h2>
        </div>

        <div className="classic-timeline">
          {careerWorkHistory.map((job, i) => (
            <div
              key={`${job.company}-${job.title}`}
              className={`classic-timeline-item${i === 0 ? ' is-current' : ''}`}
            >
              <div className="classic-timeline-dot" />
              <div className="classic-timeline-card">

                <div className="classic-timeline-header">
                  <div>
                    <h3 className="classic-job-title">{job.title}</h3>
                    <p className="classic-job-company">{job.company}</p>
                  </div>
                  <div className="classic-job-meta">
                    <span className="classic-mono-text">{job.date}</span>
                    {job.type && (
                      <span className="classic-job-type-badge">{job.type}</span>
                    )}
                  </div>
                </div>

                {job.achievements && job.achievements.length > 0 && (
                  <ul className="classic-job-achievements">
                    {job.achievements.map((ach, j) => (
                      <li key={j}>{ach}</li>
                    ))}
                  </ul>
                )}

              </div>
            </div>
          ))}
        </div>
      </div>
    </RevealSection>
  );
}

/* ── Projects ───────────────────────────────────────────────────────────── */

function ProjectsSection() {
  return (
    <RevealSection id="projects" className="classic-projects-section">
      <div className="classic-section-inner">
        <div className="classic-section-header">
          <span className="classic-section-label">// 03</span>
          <h2 className="classic-section-title">Projects</h2>
          <a
            href={projectsGithubLink}
            className="classic-github-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            View all on GitHub ↗
          </a>
        </div>

        <div className="classic-projects-grid">
          {projectsItems.map(project => (
            <a
              key={project.title}
              href={project.link}
              className="classic-project-card"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="classic-project-card-top">
                <span className="classic-project-folder" aria-hidden="true">⬡</span>
                <span className="classic-project-ext-link" aria-hidden="true">↗</span>
              </div>

              <h3 className="classic-project-title">{project.title}</h3>
              <p className="classic-project-desc">{project.descriptions[0]}</p>

              <div className="classic-project-tags">
                {(PROJECT_TECH[project.title] || []).map(tag => (
                  <span key={tag} className="classic-project-tag">{tag}</span>
                ))}
              </div>
            </a>
          ))}
        </div>
      </div>
    </RevealSection>
  );
}

/* ── Hobbies ────────────────────────────────────────────────────────────── */

function HobbiesSection() {
  return (
    <RevealSection id="hobbies" className="classic-hobbies-section">
      <div className="classic-section-inner">
        <div className="classic-section-header">
          <span className="classic-section-label">// 04</span>
          <h2 className="classic-section-title">Hobbies</h2>
        </div>

        <p className="classic-hobbies-intro">{hobbiesDescription}</p>

        <div className="classic-hobbies-grid">
          {hobbyItems.map(hobby => {
            const desc = hobby.descriptions.find(
              d => d && !d.toLowerCase().includes('click')
            );
            const Wrapper = hobby.link ? 'a' : 'div';
            const linkProps = hobby.link
              ? { href: hobby.link, target: '_blank', rel: 'noopener noreferrer' }
              : {};

            return (
              <Wrapper
                key={hobby.title}
                className="classic-hobby-card"
                {...linkProps}
              >
                <span className="classic-hobby-icon" aria-hidden="true">
                  {HOBBY_ICONS[hobby.iconKey] || '🎯'}
                </span>
                <h3 className="classic-hobby-name">{hobby.title}</h3>
                {desc && <p className="classic-hobby-desc">{desc}</p>}
              </Wrapper>
            );
          })}
        </div>
      </div>
    </RevealSection>
  );
}

/* ── Contact ────────────────────────────────────────────────────────────── */

function ContactSection() {
  return (
    <RevealSection id="contact" className="classic-contact-section">
      <div className="classic-section-inner classic-contact-inner">
        <div className="classic-section-header">
          <span className="classic-section-label">// 05</span>
          <h2 className="classic-section-title">Get In Touch</h2>
        </div>

        <p className="classic-contact-text">
          Have a project in mind or want to discuss opportunities? <br />
          I look forward to hearing from you.
        </p>

        <ContactLinks />
      </div>
    </RevealSection>
  );
}

/* ── Footer ─────────────────────────────────────────────────────────────── */

function ClassicFooter() {
  return (
    <footer className="classic-footer">
      <p className="classic-footer-text">
        &copy; {new Date().getFullYear()} {aboutProfile.name} 
      </p>
    </footer>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */

function ClassicScrollPage() {
  return (
    <div className="classic-scroll-page">
      <HeroSection />
      <hr className="classic-section-rule" />
      <SkillsSection />
      <hr className="classic-section-rule" />
      <ExperienceSection />
      <hr className="classic-section-rule" />
      <ProjectsSection />
      <hr className="classic-section-rule" />
      <HobbiesSection />
      <ContactSection />
      <ClassicFooter />
    </div>
  );
}

export default ClassicScrollPage;
